import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Search, Filter, X, Flag, Plus } from 'lucide-react';

// Glassmorphism badges - works in dark & light mode
const statusStyles = {
  open: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  "in-progress": "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  pending: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  resolved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  closed: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20",
};

const priorityStyles = {
  high: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  medium: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  low: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
};

export function RecentTickets({ tickets = [], onCreate }) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // Format time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Use provided tickets only, no demo data
  const ticketData = tickets && Array.isArray(tickets) ? tickets : [];

  const handleTicketClick = (ticket) => {
    // Navigate to ticket detail page
    navigate(`/tickets/${ticket.id}`);
  };

  // Filter tickets based on search and filters
  const filteredTickets = ticketData.filter(ticket => {
    const matchesSearch = searchTerm === '' || 
      (ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       ticket.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
      ticket.status?.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesPriority = priorityFilter === 'all' || 
      ticket.priority?.toLowerCase() === priorityFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || priorityFilter !== 'all';

  return (
    <div 
      className={cn(
        "rounded-2xl bg-white dark:bg-slate-800 border border-gray-200/50 dark:border-gray-700/50 p-6 sm:p-7 transition-all duration-300 relative overflow-hidden shadow-lg shadow-blue-500/5 h-full flex flex-col",
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}
    >

      <div className="mb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Tickets</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 transition-all duration-200 px-3 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 hover:shadow-sm hover:shadow-indigo-500/10 touch-manipulation self-start sm:self-auto"
              style={{ minHeight: '36px' }}
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>

        {/* Search and Filters Below Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-3 md:mb-4">
          {/* Search Input */}
          <div className="flex-1 relative min-w-0 w-full md:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-2.5 border border-gray-200 dark:border-slate-600 rounded-md text-sm md:text-base bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-400 touch-manipulation transition-colors"
              style={{ minHeight: '44px' }}
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 pointer-events-none z-10" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs md:text-sm pl-8 md:pl-9 pr-2 md:pr-3 py-2 md:py-2.5 border border-gray-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-400 appearance-none touch-manipulation transition-colors"
                style={{ minHeight: '44px' }}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div className="relative">
              <Flag className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 md:w-4 md:h-4 text-red-600 dark:text-red-400 pointer-events-none z-10" />
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="text-xs md:text-sm pl-8 md:pl-9 pr-2 md:pr-3 py-2 md:py-2.5 border border-gray-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-400 appearance-none touch-manipulation transition-colors"
                style={{ minHeight: '44px' }}
              >
                <option value="all">All Priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {hasActiveFilters && (
          <p className="text-xs text-gray-500 mb-2">
            Showing {filteredTickets.length} of {ticketData.length} tickets
          </p>
        )}
      </div>

      <div className="space-y-3">
        {filteredTickets.length > 0 ? (
          filteredTickets.map((ticket) => (
          <div 
            key={ticket.id || ticket.ticket_number}
            onClick={() => handleTicketClick(ticket)}
            className="group relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 transition-all duration-300 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-gradient-to-br hover:from-indigo-50/50 hover:to-transparent dark:hover:from-indigo-950/20 dark:hover:to-transparent hover:shadow-lg hover:shadow-indigo-500/10 dark:hover:shadow-indigo-500/20 hover:-translate-y-1 gap-2 sm:gap-0 touch-manipulation overflow-hidden"
            style={{ minHeight: '60px' }}
          >
            {/* Hover left stripe */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-blue-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-mono text-indigo-600 dark:text-indigo-400 font-medium">
                  {ticket.ticket_number || `TKT-${ticket.id}`}
                </span>
                <span 
                  className={cn(
                    "px-3 py-1 text-xs font-semibold rounded-full border uppercase tracking-wide",
                    statusStyles[ticket.status?.toLowerCase()] || statusStyles.open
                  )}
                >
                  {ticket.status?.replace('_', ' ').toUpperCase() || 'OPEN'}
                </span>
                <span 
                  className={cn(
                    "px-3 py-1 text-xs font-semibold rounded-full border uppercase tracking-wide",
                    priorityStyles[ticket.priority?.toLowerCase()] || priorityStyles.low
                  )}
                >
                  {ticket.priority?.toUpperCase() || 'LOW'}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {ticket.subject || 'No subject'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(ticket.customer_name || 'Unknown Customer').replace(/0+$/, '').trim()}
              </p>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 sm:ml-4 whitespace-nowrap font-medium self-end sm:self-auto">
              {getTimeAgo(ticket.created_at)}
            </span>
          </div>
        ))
        ) : (
          <div className="text-center py-8">
            {ticketData.length === 0 ? (
              <div>
                <p className="text-gray-500 text-sm mb-4">No tickets in this window.</p>
                {onCreate && (
                  <button
                    onClick={onCreate}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors shadow-lg shadow-indigo-500/20"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Create Your First Ticket
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="text-gray-500 text-sm">No tickets found matching your filters.</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium transition-colors duration-200"
                  >
                    Clear filters to see all tickets
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

